import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IStepOption, TourService } from 'ngx-ui-tour-primeng';

import { StorageManager } from 'src/app/common/storage-manager.class';
import { DashboardService } from 'src/app/service/events/dashboard.service';
import { environment } from 'src/environments/environment';

const TOUR_DELAY_AFTER_NAVIGATION_MS = 450;
const TOUR_ASYNC_STEP_TIMEOUT_MS = 3500;
const TOUR_START_DELAY_MS = 700;
const TOUR_STORAGE_KEY_PREFIX = 'farmerOnboardingTourCompleted';
const TOUR_VERSION = 'v3';
const DEFAULT_STEP_MIN_WIDTH = '300px';
const DEFAULT_STEP_MAX_WIDTH = '460px';
const DASHBOARD_SEGMENT = 'dashboard';
const DEFAULT_ROUTE = '/dashboard/inicio';
const CHAT_STEP_ROUTE = DEFAULT_ROUTE;

type TourKind = 'general' | 'module';

@Injectable({
	providedIn: 'root',
})
export class FarmerOnboardingTourService {
	private readonly tourService = inject(TourService);

	private readonly dashboardService = inject(DashboardService);

	private readonly router = inject(Router);

	private endSubscriptionRegistered = false;

	private activeTourKind: TourKind = 'general';

	public initialize(): void {
		this.initializeTour(this.buildGeneralWorkflowSteps(), 'general');
	}

	public start(): void {
		this.startGeneralTour();
	}

	public startGeneralTour(): void {
		this.initializeTour(this.buildGeneralWorkflowSteps(), 'general');
		this.tourService.start();
	}

	public startCurrentModuleTour(): void {
		this.initializeTour(this.buildCurrentModuleSteps(), 'module');
		this.tourService.start();
	}

	public startCurrentFormTour(): void {
		this.initializeTour(this.buildCurrentFormSteps(), 'module');
		this.tourService.start();
	}

	public startIfPending(): void {
		if (this.isCompleted()) {
			return;
		}

		this.initializeTour(this.buildGeneralWorkflowSteps(), 'general');

		setTimeout((): void => {
			this.tourService.start();
		}, TOUR_START_DELAY_MS);
	}

	public markAsPending(): void {
		localStorage.removeItem(this.getStorageKey());
	}

	private initializeTour(steps: IStepOption[], tourKind: TourKind): void {
		this.activeTourKind = tourKind;

		this.tourService.initialize(steps, {
			asyncStepTimeout: TOUR_ASYNC_STEP_TIMEOUT_MS,
			delayAfterNavigation: TOUR_DELAY_AFTER_NAVIGATION_MS,
			duplicateAnchorHandling: 'registerFirst',
			enableBackdrop: true,
			isAsync: true,
			isOptional: true,
			nextBtnTitle: 'Siguiente',
			prevBtnTitle: 'Anterior',
			endBtnTitle: 'Terminar',
			stepDimensions: {
				minWidth: DEFAULT_STEP_MIN_WIDTH,
				maxWidth: DEFAULT_STEP_MAX_WIDTH,
				width: 'auto',
			},
			popoverClass: 'farmer-tour-popover',
			showProgress: true,
		});

		this.registerEndSubscriptionOnce();
	}

	private registerEndSubscriptionOnce(): void {
		if (this.endSubscriptionRegistered) {
			return;
		}

		this.tourService.end$.subscribe((): void => {
			if (this.activeTourKind === 'general') {
				localStorage.setItem(this.getStorageKey(), 'true');
			}
		});

		this.endSubscriptionRegistered = true;
	}

	private buildGeneralWorkflowSteps(): IStepOption[] {
		const clientName = environment.features.clienteName.toLowerCase();
		const fincaName = environment.features.fincaName.toLowerCase();
		const parcelaName = environment.features.parcelaName.toLowerCase();

		const steps: IStepOption[] = [
			{
				stepId: 'general-home-logo',
				anchorId: 'farmerTour.homeLogo',
				title: 'Bienvenido a AgroGEST SIEX',
				content:
					'Te ayudaremos a revisar la explotación, importar datos del REA y preparar el cuaderno paso a paso.',
				route: DEFAULT_ROUTE,
			},
			{
				stepId: 'general-client-selector',
				anchorId: 'farmerTour.clientSelector',
				title: `Revisa que estás en el ${clientName} correcto`,
				content: `Antes de meter datos, comprueba este punto. Así no mezclarás parcelas o trabajos de otro ${clientName}.`,
			},
			{
				stepId: 'general-side-menu',
				anchorId: 'farmerTour.sideMenu',
				title: 'Menú principal',
				content:
					'Este menú es como el índice de una libreta: explotaciones, parcelas, personas, máquinas, productos y trabajos.',
			},
		];

		this.pushStepWhenRouteExists(steps, ['finca'], {
			stepId: 'general-create-finca',
			anchorId: 'farmerTour.addButton',
			title: `1. Crea la ${fincaName}`,
			content: `Empieza por la ${fincaName}. Es la explotación donde se juntan tus tierras, parcelas y trabajos.`,
		});

		this.pushStepWhenRouteExists(steps, ['finca'], {
			stepId: 'general-import-rea',
			anchorId: 'farmerTour.reaImportButton',
			title: 'Autoriza e importa datos del REA',
			content: 'Para importar el REA o enviar el CUE, primero debes autorizar a la aplicación para consultar y tratar esos datos.',
			duplicateAnchorHandling: 'registerFirst',
		});

		this.pushStepWhenRouteExists(steps, ['parcelas'], {
			stepId: 'general-create-parcelas',
			anchorId: 'farmerTour.addButton',
			title: `2. Añade tus ${parcelaName}s`,
			content: `Crea cada ${parcelaName} donde trabajas. Después podrás elegirla al apuntar tratamientos o abonados.`,
		});

		this.pushStepWhenRouteExists(steps, ['sectores'], {
			stepId: 'general-create-sectores',
			anchorId: 'farmerTour.addButton',
			title: '3. Junta parcelas si trabajas varias igual',
			content: 'Este paso es opcional. Sirve para agrupar parcelas con el mismo cultivo, riego o manejo.',
		});

		this.pushStepWhenRouteExists(steps, ['trabajadores'], {
			stepId: 'general-workers',
			anchorId: 'farmerTour.addButton',
			title: '4. Añade las personas que trabajan',
			content: 'Apunta quién hace, revisa o aplica cada trabajo. Así quedará claro quién participó.',
		});

		this.pushStepWhenRouteExists(steps, ['maquinaria'], {
			stepId: 'general-machinery',
			anchorId: 'farmerTour.addButton',
			title: 'Añade tus máquinas',
			content: 'Registra tractores, cubas, aperos o equipos. Luego podrás elegirlos al apuntar un trabajo.',
		});

		this.pushStepWhenRouteExists(steps, ['productos-fitosanitarios'], {
			stepId: 'general-phyto-products',
			anchorId: 'farmerTour.addButton',
			title: 'Añade productos para tratamientos',
			content: 'Busca el producto antes de usarlo. Revisa siempre que esté autorizado y que la dosis sea correcta.',
		});

		this.pushStepWhenRouteExists(steps, ['productos-fertilizantes'], {
			stepId: 'general-ferti-products',
			anchorId: 'farmerTour.addButton',
			title: 'Añade abonos y fertilizantes',
			content: 'Registra los abonos que usas en campo. Después podrás elegirlos al apuntar una fertilización.',
		});

		this.pushStepWhenRouteExists(steps, ['fitosanitarios'], {
			stepId: 'general-phyto-task',
			anchorId: 'farmerTour.addButton',
			title: '5. Apunta un tratamiento',
			content: 'Prepara antes la parcela, la fecha, el producto, la persona que aplica y la máquina si se ha usado.',
		});

		this.pushStepWhenRouteExists(steps, ['fertilizantes'], {
			stepId: 'general-ferti-task',
			anchorId: 'farmerTour.addButton',
			title: 'Apunta una fertilización',
			content: 'Indica qué abono has usado, dónde lo has aplicado, cuándo y con qué cantidad.',
		});

		this.pushStepWhenRouteExists(steps, ['finca'], {
			stepId: 'general-cuaderno',
			anchorId: 'farmerTour.cuadernoButton',
			title: '6. Genera el cuaderno en Word',
			content: 'Cuando tengas los trabajos apuntados, genera el cuaderno para revisarlo, guardarlo o imprimirlo.',
			duplicateAnchorHandling: 'registerFirst',
		});

		this.pushStepWhenRouteExists(steps, ['finca'], {
			stepId: 'general-cue',
			anchorId: 'farmerTour.cueButton',
			title: 'Comprueba y envía el CUE',
			content: 'Antes de enviar, revisa los avisos. Si falta algún dato, la aplicación te indicará dónde corregirlo.',
			duplicateAnchorHandling: 'registerFirst',
		});

		steps.push({
			stepId: 'general-module-info',
			anchorId: 'farmerTour.moduleInfoButton',
			title: 'Ayuda de esta pantalla',
			content: 'Pulsa este botón si quieres una explicación sencilla de la pantalla donde estás.',
			route: this.router.url,
		});

		steps.push({
			stepId: 'general-form-help',
			anchorId: 'farmerTour.contentArea',
			title: 'Ayuda al rellenar datos',
			content:
				'Cuando crees o cambies un registro, podrás activar ayudas de cada campo: qué significa, para qué sirve y un ejemplo.',
			route: this.router.url,
		});

		steps.push({
			stepId: 'general-chatbot-help',
			anchorId: 'farmerTour.chatbotHelp',
			title: 'Chat de ayuda',
			content: 'Usa el icono de chat de ayuda, situado en la esquina inferior derecha, si te atascas. Escribe dudas concretas, por ejemplo: no encuentro mi parcela.',
			route: CHAT_STEP_ROUTE,
		});

		return steps;
	}

	private buildCurrentModuleSteps(): IStepOption[] {
		const currentRouteId = this.getCurrentRouteId();

		switch (currentRouteId) {
			case 'finca':
				return this.buildFincasModuleSteps();
			case 'parcelas':
				return this.buildParcelasModuleSteps();
			case 'sectores':
				return this.buildSectoresModuleSteps();
			case 'trabajadores':
				return this.buildTrabajadoresModuleSteps();
			case 'maquinaria':
				return this.buildMaquinariaModuleSteps();
			case 'productos-fitosanitarios':
				return this.buildProductosFitosanitariosModuleSteps();
			case 'productos-fertilizantes':
				return this.buildProductosFertilizantesModuleSteps();
			case 'fitosanitarios':
				return this.buildTareasFitosanitariasModuleSteps();
			case 'fertilizantes':
				return this.buildTareasFertilizantesModuleSteps();
			case 'tratamientos':
				return this.buildTratamientosModuleSteps();
			default:
				return this.buildFallbackModuleSteps();
		}
	}

	private buildCurrentFormSteps(): IStepOption[] {
		return [
			{
				stepId: 'form-header',
				anchorId: 'farmerTour.formHeader',
				title: 'Comprueba qué estás rellenando',
				content: 'Primero lee el título. Así sabrás si estás creando o editando una finca, parcela, recurso o tarea.',
				route: this.router.url,
			},
			{
				stepId: 'form-guide-button',
				anchorId: 'farmerTour.formGuideButton',
				title: 'Guía del formulario',
				content: 'Pulsa este botón cuando quieras repasar cómo se rellena esta pantalla sin salir del formulario.',
			},
			{
				stepId: 'form-fields',
				anchorId: 'farmerTour.formSections',
				title: 'Campos en lenguaje sencillo',
				content:
					'Cada campo usa una explicación práctica. Cuando exista un término oficial, lo verás como ayuda secundaria.',
			},
			{
				stepId: 'form-field-help',
				anchorId: 'farmerTour.fieldHelpToggle',
				title: 'Ayuda campo por campo',
				content:
					'Activa este botón si quieres ver ayudas campo por campo. Después podrás abrir la ayuda concreta de cada dato.',
				duplicateAnchorHandling: 'registerFirst',
			},
			{
				stepId: 'form-actions',
				anchorId: 'farmerTour.formActions',
				title: 'Guardar, corregir o volver',
				content:
					'Si aparece un error, revisa solo el campo marcado. El mensaje te dirá qué pasa y qué debes hacer ahora.',
			},
		];
	}

	private buildFincasModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Explotaciones',
      'Aquí empieza todo. Crea o revisa tu explotación antes de añadir parcelas, personas, máquinas o trabajos.'
    ),
    {
      stepId: 'fincas-search',
      anchorId: 'farmerTour.search',
      title: 'Busca una explotación',
      content: 'Escribe parte del nombre para encontrarla rápido.',
    },
    {
      stepId: 'fincas-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añade una explotación',
      content: 'Pulsa aquí para crearla. Rellena los datos principales y guarda antes de seguir con las parcelas.',
    },
    {
      stepId: 'fincas-rea',
      anchorId: 'farmerTour.reaImportButton',
      title: 'Traer datos del REA',
      content: 'El REA es el registro oficial de explotaciones. Para traer datos, el agricultor debe autorizarlo.',
      duplicateAnchorHandling: 'registerFirst',
    },
    {
      stepId: 'fincas-cuaderno',
      anchorId: 'farmerTour.cuadernoButton',
      title: 'Descargar cuaderno',
      content: 'Cuando tengas trabajos apuntados, crea el cuaderno en Word para revisarlo, guardarlo o imprimirlo.',
      duplicateAnchorHandling: 'registerFirst',
    },
    {
      stepId: 'fincas-cue',
      anchorId: 'farmerTour.cueButton',
      title: 'Comprobar y enviar CUE',
      content: 'La respuesta recibida indicará si el cuaderno se ha aceptado correctamente o si debes revisar algún dato.',
      duplicateAnchorHandling: 'registerFirst',
    },
    this.createRowActionsStep(
      'Botones de explotación',
      'El botón de información muestra detalles, el lápiz cambia datos y la cruz borra solo si estás seguro.'
    ),
  ];
}

private buildParcelasModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Parcelas',
      'Añade las zonas de trabajo de tu explotación. Cada parcela es un trozo de campo que quieres controlar.'
    ),
    this.createSearchStep('Busca por nombre, explotación, cultivo o cualquier dato que veas en la tabla.'),
    {
      stepId: 'parcelas-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añade una parcela',
      content: 'Crea una parcela por cada zona que trabajas. Luego podrás elegirla en tratamientos y abonados.',
    },
    this.createTableStep('Revisa la superficie, el cultivo y la explotación antes de usar la parcela en un trabajo.'),
    this.createRowActionsStep(
      'Cambia una parcela',
      'Corrige superficie, cultivo o datos SIGPAC para evitar errores al apuntar trabajos.'
    ),
  ];
}

private buildSectoresModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Sectores',
      'Los sectores sirven para juntar parcelas que trabajas igual. Es opcional, pero ahorra tiempo.'
    ),
    {
      stepId: 'sectores-add',
      anchorId: 'farmerTour.addButton',
      title: 'Crea un grupo de parcelas',
      content: 'Agrupa parcelas con algo en común, por ejemplo el mismo cultivo, variedad, riego o manejo.',
    },
    this.createTableStep('Comprueba que cada grupo representa bien cómo trabajas en el campo.'),
    this.createRowActionsStep(
      'Actualiza grupos',
      'Cambia el grupo cuando cambie el cultivo, el riego o las parcelas que lo forman.'
    ),
  ];
}

private buildTrabajadoresModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Personas',
      'Registra las personas que participan en los trabajos para saber quién hizo cada tarea.'
    ),
    {
      stepId: 'trabajadores-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añade una persona',
      content: 'Da de alta aplicadores, responsables o cualquier persona que participe en tratamientos o labores.',
    },
    this.createTableStep('Revisa que los nombres y datos estén bien para poder elegirlos después.'),
    this.createRowActionsStep(
      'Mantén los datos al día',
      'Cambia los datos si cambia la persona, su identificación o el papel que tiene en la explotación.'
    ),
  ];
}

private buildMaquinariaModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Maquinaria',
      'Aquí apuntas las máquinas y equipos que usas en el campo.'
    ),
    {
      stepId: 'maquinaria-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añade una máquina',
      content: 'Crea tractores, cubas, equipos de aplicación, aperos u otras máquinas que uses en los trabajos.',
    },
    this.createTableStep('Comprueba que cada máquina esté bien identificada para elegirla luego sin dudas.'),
    this.createRowActionsStep(
      'Cambia una máquina',
      'Actualiza los datos si cambias de equipo o necesitas corregir alguna información.'
    ),
  ];
}

private buildProductosFitosanitariosModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Productos para tratamientos',
      'Prepara los productos que usarás contra plagas, enfermedades o malas hierbas.'
    ),
    this.createSearchStep('Busca el producto por nombre, referencia o materia activa antes de añadirlo a un trabajo.'),
    {
      stepId: 'productos-fito-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añade un producto',
      content: 'Busca el producto oficial y revisa siempre autorización, cultivo y dosis antes de usarlo.',
    },
    this.createTableStep('Consulta los productos disponibles y confirma que son los que realmente usarás.'),
    this.createRowActionsStep(
      'Revisa productos',
      'Usa información para ver detalles y el lápiz para corregir datos.'
    ),
  ];
}

private buildProductosFertilizantesModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Abonos y fertilizantes',
      'Registra los abonos que usas para poder apuntar después las fertilizaciones.'
    ),
    this.createSearchStep('Busca el fertilizante por nombre o composición cuando la lista sea larga.'),
    {
      stepId: 'productos-ferti-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añade un fertilizante',
      content: 'Crea el fertilizante con los datos necesarios para poder elegirlo después en una tarea.',
    },
    this.createTableStep('Revisa los productos creados antes de apuntar una aplicación en campo.'),
    this.createRowActionsStep(
      'Corrige fertilizantes',
      'Edita el fertilizante si cambia el nombre, la composición o cualquier dato importante.'
    ),
  ];
}

private buildTareasFitosanitariasModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Tratamientos',
      'Apunta los tratamientos hechos o planificados contra plagas, enfermedades o malas hierbas.'
    ),
    {
      stepId: 'tareas-fito-add',
      anchorId: 'farmerTour.addButton',
      title: 'Apunta un tratamiento',
      content: 'Antes de empezar, ten preparada la parcela, fecha, producto, aplicador y máquina si se ha usado.',
    },
    this.createTableStep('La tabla te ayuda a revisar fecha, parcela, producto, persona que aplica y otros datos.'),
    this.createRowActionsStep(
      'Corrige un tratamiento',
      'Revisa dosis, fecha, producto o parcela antes de descargar el cuaderno o enviar el CUE.'
    ),
  ];
}

private buildTareasFertilizantesModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Fertilizaciones',
      'Aquí apuntas los abonados y fertilizantes aplicados en la explotación.'
    ),
    {
      stepId: 'tareas-ferti-add',
      anchorId: 'farmerTour.addButton',
      title: 'Apunta una fertilización',
      content: 'Elige explotación, parcela o grupo, producto, fecha y cantidad aplicada.',
    },
    this.createTableStep('Revisa cantidades, fechas y superficies antes de dar el trabajo por bueno.'),
    this.createRowActionsStep(
      'Cambia una fertilización',
      'Corrige cualquier dato antes de preparar documentación o envío oficial.'
    ),
  ];
}

private buildTratamientosModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Tratamientos',
      'Esta pantalla junta tratamientos para que puedas consultarlos y revisarlos.'
    ),
    {
      stepId: 'tratamientos-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añade un tratamiento',
      content: 'Apunta la información necesaria para dejar claro qué trabajo se hizo, dónde, cuándo y con qué producto.',
    },
    this.createTableStep('Revisa el listado para comprobar que cada tratamiento tiene sus datos principales.'),
    this.createRowActionsStep(
      'Revisa, cambia o borra',
      'Consulta detalles, corrige errores o borra solo cuando estés seguro.'
    ),
  ];
}

private buildFallbackModuleSteps(): IStepOption[] {
  return [
    this.createModuleIntroStep(
      'Guía de esta pantalla',
      'Aquí verás los elementos habituales: buscar, añadir, revisar una tabla y usar botones de cada fila.'
    ),
    this.createSearchStep('Escribe una palabra para encontrar antes el registro que necesitas.'),
    {
      stepId: 'fallback-add',
      anchorId: 'farmerTour.addButton',
      title: 'Añadir nuevo registro',
      content: 'Si ves este botón, sirve para crear un dato nuevo en esta pantalla.',
    },
    this.createTableStep('La tabla muestra los datos principales para que puedas revisarlos.'),
    this.createRowActionsStep(
      'Botones de cada fila',
      'Usa los botones para ver detalles, cambiar datos o borrar según tus permisos.'
    ),
  ];
}

	private createModuleIntroStep(title: string, content: string): IStepOption {
		return {
			stepId: 'module-intro',
			anchorId: 'farmerTour.moduleInfoButton',
			title,
			content,
			route: this.router.url,
		};
	}

	private createSearchStep(content: string): IStepOption {
		return {
			stepId: 'module-search',
			anchorId: 'farmerTour.search',
			title: 'Busca rápido',
			content,
		};
	}

	private createTableStep(content: string): IStepOption {
		return {
			stepId: 'module-table',
			anchorId: 'farmerTour.table',
			title: 'Revisa el listado o crea el primer registro',
			content: `${content} Si la lista está vacía, usa el botón de añadir para crear el primer registro.`,
		};
	}

	private createRowActionsStep(title: string, content: string): IStepOption {
		return {
			stepId: 'module-row-actions',
			anchorId: 'farmerTour.rowActions',
			title,
			content,
			duplicateAnchorHandling: 'registerFirst',
		};
	}

	private pushStepWhenRouteExists(steps: IStepOption[], preferredIds: string[], step: IStepOption): void {
		const route = this.getFirstAvailableRoute(preferredIds);

		if (route === null) {
			return;
		}

		steps.push({
			...step,
			route,
		});
	}

	private getCurrentRouteId(): string {
		const path = this.router.url.split('?')[0]?.split('#')[0];
		const segments = path?.split('/').filter((segment) => segment.length > 0);
		const dashboardIndex = segments?.indexOf(DASHBOARD_SEGMENT);

		if (dashboardIndex && dashboardIndex < 0) {
			return 'inicio';
		}

		return segments?.[(dashboardIndex ?? 0) + 1] ?? 'inicio';
	}

	private getFirstAvailableRoute(preferredIds: string[]): string | null {
		const availableRoutes = this.dashboardService.menuItemData.flatMap((item) => {
			if (item.visible === false) {
				return [];
			}

			const routes = [item.id];
			const childRoutes = item.expandable?.filter((child) => child.visible !== false).map((child) => child.id) ?? [];

			return routes.concat(childRoutes);
		});

		const routeId = preferredIds.find((id) => availableRoutes.includes(id));

		return routeId ? `/dashboard/${routeId}` : null;
	}

	private isCompleted(): boolean {
		return localStorage.getItem(this.getStorageKey()) === 'true';
	}

	private getStorageKey(): string {
		const userId = StorageManager.getUser().id ?? 'anonymous';
		const tourId = this.activeTourKind === 'module' ? this.getCurrentRouteId() : 'general';

		return [TOUR_STORAGE_KEY_PREFIX, TOUR_VERSION, environment.appName, userId, tourId].join(':');
	}
}
